import React, { useEffect, useMemo, useState } from 'react';

import { Tabs } from '@chakra-ui/react';
import IconDropdown from 'components/IconDropdown';
import NotificationsWrapper from 'components/Notifications/NotificationsWrapper';
import { NotificationsProvider } from 'components/Notifications/context';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledIconButton, StyledTypography, StyledDivider, VerticallyAlignedFlexContainer } from 'styles/common';
import {
  checkLevelUpPageFeatureFlagEnabled,
  checkPersonaInsightsEnabled,
  checkVisierAnalyticsEnabled,
} from 'utils/helpers/featureFlags';
import {
  checkFeedbackModuleEnabled,
  checkGoalModuleEnabled,
  checkLevelUpModuleEnabled,
  checkOneOnOneModuleEnabled,
  checkReviewModuleEnabled,
  checkTaskModuleEnabled,
} from 'utils/helpers/moduleAccess';
import { checkUserIsAnyModuleAdmin, checkUserIsHRISManager } from 'utils/helpers/rolesAccess';

import {
  CoachingIconDualTone,
  CoachingIconHover,
  CoachingIconSelected,
  GoalIconDualTone,
  GoalIconHover,
  GoalIconSelected,
  HomeIconDualTone,
  HomeIconHover,
  HomeIconSelected,
  InitiativesIconDualTone,
  InitiativesIconHover,
  InitiativesIconSelected,
  LevelUpIconDualTone,
  LevelUpIconHover,
  LevelUpIconSelected,
  OneOnOneIconDualTone,
  OneOnOneIconHover,
  OneOnOneIconSelected,
  PerformanceIconDualTone,
  PerformanceIconHover,
  PerformanceIconSelected,
} from 'assets/assetsV1/iconComponents';

import { FEATURE_KEYS, MODULE_KEYS_USER_PREFERENCE_PERSISTENCE, TEAM_TYPE } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import {
  goToHome,
  getQueryParams,
  goToLevelUpPage,
  getPersistenceKey,
  getMatchesForCurrentRoute,
} from 'constants/helper';
import { GoalsNavSvgIcon, TasksIcon, ArrowDownIcon } from 'constants/icons';
import { routes } from 'constants/routes';

import { NavItem } from 'uiToolkitV1/NavBar';
import { NavMenuItem, SubNavItem } from 'uiToolkitV1/NavBar/NavMenuItem';

import AnalyticsHeader from './AnalyticsHeader';
import EngagementHeader from './EngagementHeader';
import { FeedbackSubMenu } from './FeedbackSubMenu';
import PersonaInsightsHeader from './PersonaInsightsHeader';
import ReviewNotificationSubMenu from './ReviewNotificationSubMenu';
import UserSettingsDropdownRabbit from './UserSettingsDropdownRabbit';
// import InsightsDropdown, { getEnabledMenuItems } from './InsightsDropdown';
import { HeaderIconContainer, TextAndArrowIconContainer } from './common/styles';

export const MY_GOALS = 'My Goals';

export const PERSONAL_INITIATIVES = 'My Initiatives';

export function GoalsOrInitiativesButton({
  onClick,
  isTeamMatch,
  onlyOneTeamExists,
  heading = 'Goals',
  isGoals = false,
  anchorEl = null,
  className = '',
  ...props
}) {
  const isActive = isTeamMatch || anchorEl;
  return (
    <HeaderIconContainer className={className} onClick={onClick}>
      <StyledIconButton {...props} className={`onboardingStep01 ${props.className}`}>
        {isGoals ? (
          <GoalsNavSvgIcon size="headerSmall" color={isActive ? 'primary' : 'grey'} />
        ) : (
          <TasksIcon size="headerSmall" color={isActive ? 'primary' : 'grey'} />
        )}
      </StyledIconButton>
      <StyledTypography variant="body2" color={isActive ? 'primary' : 'grey'}>
        <TextAndArrowIconContainer isSelected={isActive}>
          {heading}
          {!onlyOneTeamExists && (
            // <IconContainer color={isTeamMatch ? 'primary' : 'grey'}>
            <ArrowDownIcon size="tiniest" color={isActive ? 'primary' : 'grey'} leftGutter={1} />
            // </IconContainer>
          )}
        </TextAndArrowIconContainer>
      </StyledTypography>
    </HeaderIconContainer>
  );
}

function HeaderActions({ history, location, ...props }) {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const dispatch = useDispatch();

  const {
    isHomeMatch,
    isTeamMatch,
    isOneOnOneMatch,
    isLevelUpMatch,
    isGoalsSettingsMatch,
    isInitiativesSettingsMatch,
    isReviewMatch,
    isAnalyticsV0Match,
    isFeedbackMatch,
  } = getMatchesForCurrentRoute(location.pathname, location);

  const { search: queryString } = location;
  const { isGoalsPage, isInitiativesPage } = getQueryParams(queryString);

  const [employeeTeams, setEmployeeTeams] = useState(loggedInEmployee.teams ?? []);

  const putPersonalTeamToFirstPosition = () => {
    let personalTeamIndex = null;
    const personalTeam = employeeTeams?.find((team, index) => {
      if (team?.teamType === TEAM_TYPE.personal) {
        personalTeamIndex = index;
        return team;
      }
    });

    if (personalTeam) {
      const teamsArray = [...employeeTeams];
      teamsArray.splice(personalTeamIndex, 1);
      teamsArray.splice(0, 0, personalTeam);
      setEmployeeTeams(teamsArray);
    }
  };

  useEffect(() => {
    putPersonalTeamToFirstPosition();
  }, [loggedInEmployee?.teams]);

  /* Grid State persistence Key */
  const persistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const tabId = 'Explore';
    const uniqueId = null;

    return getPersistenceKey({
      moduleName,
      viewId: '-12', // MY_OKRs is always -12
      tabId,
      uniqueId,
    });
  }, []);

  const goalIconClickHandler = async () => {
    history.push({
      pathname: routes.goals.list,
    });
  };

  const initiativesIconClickHandler = () => {
    history.push({
      pathname: routes.initiative.audit,
      // search: `?cohort=${cohortSearchParams}`
    });
  };

  // const teamMenuItemsForInitiatives = employeeTeams.map(team => {
  //   const isPersonalTeam = team.teamType === TEAM_TYPE.personal;

  //   const menuItem = {};
  //   menuItem.content = capitalizeShort(team.name + ' Initiatives');
  //   menuItem.onClick = () => {
  //     const url = getTeamPageForInitiativesRoute({
  //       teamId: team.id,
  //       teamName: isPersonalTeam
  //         ? PERSONAL_INITIATIVES
  //         : capitalizeShort(team.name + ' Initiatives')
  //     });
  //     history.push(url);
  //   };
  //   menuItem.visibilityType = team.visibilityType;
  //   menuItem.teamType = team.teamType;
  //   if (isPersonalTeam) {
  //     menuItem.content = capitalizeShort(PERSONAL_INITIATIVES);
  //     menuItem.showSeparator = true;
  //   }
  //   return menuItem;
  // });

  const onlyOneTeamExists = employeeTeams.length === 1;

  // const analyticsMenuItems = useMemo(() => getEnabledMenuItems({ loggedInEmployee }), [
  //   loggedInEmployee
  // ]);

  return (
    <Container>
      <Tabs>
        <IconsContainer reviewOption={loggedInEmployee.isFeatureEnabled(FEATURE_KEYS.REVIEW)}>
          <HeaderIconContainer onClick={() => goToHome(history)}>
            <NavItem
              Icon={HomeIconDualTone}
              HoverIcon={HomeIconHover}
              ActiveIcon={HomeIconSelected}
              isActive={isHomeMatch}
            >
              Home
            </NavItem>
          </HeaderIconContainer>

          {(checkGoalModuleEnabled() || checkTaskModuleEnabled() || checkReviewModuleEnabled()) && (
            <HeaderIconContainer>
              <NavMenuItem
                Icon={PerformanceIconDualTone}
                HoverIcon={PerformanceIconHover}
                ActiveIcon={PerformanceIconSelected}
                isActive={
                  (isTeamMatch && isGoalsPage) ||
                  isGoalsSettingsMatch ||
                  (isTeamMatch && isInitiativesPage) ||
                  isInitiativesSettingsMatch ||
                  (isReviewMatch && !isAnalyticsV0Match)
                }
                title="Performance"
              >
                {checkGoalModuleEnabled() && (
                  <HeaderIconContainer onClick={goalIconClickHandler}>
                    <SubNavItem
                      Icon={GoalIconDualTone}
                      HoverIcon={GoalIconHover}
                      ActiveIcon={GoalIconSelected}
                      isActive={(isTeamMatch && isGoalsPage) || isGoalsSettingsMatch}
                      title={modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}
                      subTitle="Helps you set & track your progress"
                    />
                  </HeaderIconContainer>
                )}

                {checkTaskModuleEnabled() && (
                  <HeaderIconContainer onClick={initiativesIconClickHandler}>
                    <SubNavItem
                      Icon={InitiativesIconDualTone}
                      HoverIcon={InitiativesIconHover}
                      ActiveIcon={InitiativesIconSelected}
                      isActive={(isTeamMatch && isInitiativesPage) || isInitiativesSettingsMatch}
                      title="Initiatives"
                      subTitle="Helps you create and track projects"
                    />
                  </HeaderIconContainer>
                )}
                {loggedInEmployee.isFeatureEnabled(FEATURE_KEYS.REVIEW) && <ReviewNotificationSubMenu />}
              </NavMenuItem>
            </HeaderIconContainer>
          )}

          {/* {loggedInEmployee.isFeatureEnabled(FEATURE_KEYS.REVIEW) && <ReviewNotification />} */}

          {loggedInEmployee.isFeatureEnabled(FEATURE_KEYS.ENGAGEMENT) ? <EngagementHeader /> : null}

          {(checkOneOnOneModuleEnabled() ||
            (checkLevelUpModuleEnabled() && checkLevelUpPageFeatureFlagEnabled()) ||
            checkFeedbackModuleEnabled()) && (
            <HeaderIconContainer>
              <NavMenuItem
                Icon={CoachingIconDualTone}
                HoverIcon={CoachingIconHover}
                ActiveIcon={CoachingIconSelected}
                isActive={(isOneOnOneMatch || isLevelUpMatch || isFeedbackMatch) && !isAnalyticsV0Match}
                title="Coaching"
              >
                {loggedInEmployee.isFeatureEnabled(FEATURE_KEYS.CHECK_IN) && (
                  <HeaderIconContainer onClick={() => history.push(routes.oneOnOne.audit)}>
                    <SubNavItem
                      Icon={OneOnOneIconDualTone}
                      HoverIcon={OneOnOneIconHover}
                      ActiveIcon={OneOnOneIconSelected}
                      isActive={isOneOnOneMatch && !isAnalyticsV0Match}
                      title="1:1s"
                      subTitle="Set up 1:1 conversations with Managers & team members"
                    />
                  </HeaderIconContainer>
                )}

                {checkLevelUpModuleEnabled() ? (
                  <HeaderIconContainer onClick={() => goToLevelUpPage(history)}>
                    <SubNavItem
                      Icon={LevelUpIconDualTone}
                      HoverIcon={LevelUpIconHover}
                      ActiveIcon={LevelUpIconSelected}
                      isActive={isLevelUpMatch && !isAnalyticsV0Match}
                      title="Develop"
                      subTitle="Helps employees move up in the ladder"
                    />
                  </HeaderIconContainer>
                ) : null}
                <FeedbackSubMenu />
              </NavMenuItem>
            </HeaderIconContainer>
          )}

          {checkPersonaInsightsEnabled() && (checkUserIsAnyModuleAdmin() || checkUserIsHRISManager()) && (
            <PersonaInsightsHeader />
          )}

          {checkVisierAnalyticsEnabled() && <AnalyticsHeader />}

          {/* {analyticsMenuItems?.length > 0 && <InsightsDropdown />} */}
          <VerticallyAlignedFlexContainer>
            <StyledDivider
              thickness={5}
              orientation="vertical"
              style={{ marginLeft: '0.5rem', height: '42px' }} // <- need to ask the color for it from colors.js
            />
          </VerticallyAlignedFlexContainer>
          <MyProfileContainer>
            <NotificationsProvider>
              <NotificationsWrapper>
                <UserSettingsDropdownRabbit />
              </NotificationsWrapper>
            </NotificationsProvider>
          </MyProfileContainer>
        </IconsContainer>
      </Tabs>
    </Container>
  );
}

export default withRouter(HeaderActions);

const Container = styled.div`
  flex: 2 1.5 25.5%;
  align-items: stretch;
  // padding-left: ${(props) => props.theme.space.horizontal.normal};
  // padding-right: 1rem;
  height: 100%;
  ${(props) => props.theme.breakpoints.up('xl')} {
    flex: 2 1.5 20%;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 100%;
  margin-right: 1rem;
`;

const StyledIconDropdown = styled(IconDropdown)`
  z-index: 9;
`;

const MyProfileContainer = styled('div')`
  margin-left: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
